import React, { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import { UserFormData } from "./validation.schema";
import Select from "@/components/Select/index";
import { UserStatusEnum } from "@/domain/user.enum";
import { useCountrySetting } from "@/hooks/useSetting";
import { useTranslation } from "react-i18next";

type TabUserProps = {
  editMode?: boolean;
};

export const TabUser: FC<TabUserProps> = ({ editMode = false }) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<UserFormData>();
  const { t } = useTranslation("user");
  const { t: tCommon } = useTranslation("common");

  const statusOptions = useMemo(() => {
    // AC08, AC09: In edit mode, if status is PENDING, only show PENDING option
    // Otherwise show ACTIVE and INACTIVE
    if (editMode) {
      const currentStatus = watch("status");
      if (currentStatus === UserStatusEnum.PENDING) {
        return [
          {
            key: UserStatusEnum.PENDING,
            value: tCommon(`enums.${UserStatusEnum.PENDING}` as any),
          },
        ];
      }
      return [
        {
          key: UserStatusEnum.ACTIVE,
          value: tCommon(`enums.${UserStatusEnum.ACTIVE}` as any),
        },
        {
          key: UserStatusEnum.INACTIVE,
          value: tCommon(`enums.${UserStatusEnum.INACTIVE}` as any),
        },
      ];
    }
    return [];
  }, [editMode, watch, tCommon]);

  const country = useCountrySetting();

  // Get current status to check if PENDING (AC08, AC09)
  const currentStatus = watch("status");
  const isPending = currentStatus === UserStatusEnum.PENDING;

  return (
    <Grid container spacing={2} sx={{ mt: 0 }}>
      {/* Row 1: First name, Last name */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="firstName"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="firstName"
              label={t("labels.first-name")}
              variant="outlined"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName ? errors.firstName.message : null}
              inputProps={{
                maxLength: 50,
              }}
              fullWidth
              required
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="lastName"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="lastName"
              label={t("labels.last-name")}
              variant="outlined"
              required
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName ? errors.lastName.message : null}
              inputProps={{
                maxLength: 50,
              }}
              fullWidth
            />
          )}
        />
      </Grid>
      {/* Row 2: Email, Phone number */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="email"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              id="email"
              label={t("labels.email")}
              type="email"
              required
              variant="outlined"
              value={field.value}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.email}
              helperText={errors.email ? errors.email.message : null}
              inputProps={{
                maxLength: 100,
              }}
              fullWidth
              disabled={editMode} // AC07: Email disabled in Edit mode
            />
          )}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="phoneNumber"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <PhoneNumberInput
              {...field}
              defaultCode={country}
              value={field.value || ""}
              label={t("labels.phone-number")}
              variant="outlined"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(e.target.value);
              }}
              error={!!errors.phoneNumber}
              helperText={
                errors.phoneNumber ? errors.phoneNumber.message : null
              }
              fullWidth
            />
          )}
        />
      </Grid>
      {/* Row 3: External ID, Status (only in Edit mode) */}
      <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="npi"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              id="npi"
              label={t("labels.external-id") || "External ID"}
              variant="outlined"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.npi}
              helperText={errors.npi ? errors.npi.message : null}
              inputProps={{
                maxLength: 30,
              }}
            />
          )}
        />
      </Grid>
      {editMode && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="status"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Select
                id="status-select"
                label={t("labels.status")}
                handleChange={(value) => field.onChange(value)}
                data={statusOptions}
                value={field.value as string}
                disabled={isPending} // AC08, AC09: Status disabled when Pending
                error={!!errors.status}
                errorMessage={errors.status?.message || ""}
                width="100%"
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};
